import React, { useEffect, useMemo, useState } from "react";
import { API_BASE } from "../config/api";

const GestionDocentes = () => {
  const [docentes, setDocentes] = useState([]);
  const [colegios, setColegios] = useState([]);

  const [nuevoDocente, setNuevoDocente] = useState({
    usuario: "",
    contrasena: "",
    nombre_completo: "",
    cedula: "",
    email: "",
    telefono: "",
    area: "informática",
  });

  const [docenteSeleccionado, setDocenteSeleccionado] = useState(null); // id
  const [modoEdicion, setModoEdicion] = useState(false);

  // ✅ Buscar docente
  const [busqueda, setBusqueda] = useState("");

  // ✅ Modal VER docente (solo lectura)
  const [modalVerAbierto, setModalVerAbierto] = useState(false);
  const [docenteVista, setDocenteVista] = useState(null);
  const [colegiosAsociadosVista, setColegiosAsociadosVista] = useState([]);
  const [cargandoAsociados, setCargandoAsociados] = useState(false);

  // ✅ Modal ASOCIAR colegio
  const [modalAsociarAbierto, setModalAsociarAbierto] = useState(false);
  const [docenteAsociar, setDocenteAsociar] = useState(null); // objeto docente
  const [colegiosSeleccionados, setColegiosSeleccionados] = useState([]);
  const [colegiosYaAsociados, setColegiosYaAsociados] = useState([]);
  const [busquedaColegioAsociar, setBusquedaColegioAsociar] = useState("");

  useEffect(() => {
    fetchDocentes();
    fetchColegios();
  }, []);

  // =========================
  // Fetch
  // =========================
  const fetchDocentes = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/profesores`);
      if (!res.ok) throw new Error("No se pudo cargar docentes");
      const data = await res.json();
      setDocentes(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error cargando docentes:", e);
      setDocentes([]);
    }
  };

  const fetchColegios = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/colegios`);
      if (!res.ok) throw new Error("No se pudo cargar colegios");
      const data = await res.json();
      setColegios(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error cargando colegios:", e);
      setColegios([]);
    }
  };

  // =========================
  // Helpers
  // =========================
  const resetFormulario = () => {
    setNuevoDocente({
      usuario: "",
      contrasena: "",
      nombre_completo: "",
      cedula: "",
      email: "",
      telefono: "",
      area: "informática",
    });
  };

  const manejarCambio = (e) => {
    const { name, value } = e.target;
    setNuevoDocente((prev) => ({ ...prev, [name]: value }));
  };

  // =========================
  // CRUD Docente
  // =========================
  const registrarDocente = async (e) => {
    e.preventDefault();

    const url = modoEdicion
      ? `${API_BASE}/api/profesores/${docenteSeleccionado}`
      : `${API_BASE}/api/profesores`;

    const metodo = modoEdicion ? "PUT" : "POST";

    try {
      const res = await fetch(url, {
        method: metodo,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(nuevoDocente),
      });

      if (res.ok) {
        alert(modoEdicion ? "Docente actualizado" : "Docente registrado");
        resetFormulario();
        setModoEdicion(false);
        setDocenteSeleccionado(null);
        await fetchDocentes();
      } else {
        let msg = "Error al registrar/actualizar docente";
        try {
          const data = await res.json();
          if (data?.mensaje) msg = data.mensaje;
        } catch {}
        alert(msg);
      }
    } catch (e) {
      console.error("Error registrando/actualizando docente:", e);
      alert("Error de conexión al registrar/actualizar docente");
    }
  };

  const eliminarDocente = async (id) => {
    if (!window.confirm("¿Seguro que deseas eliminar este docente?")) return;

    try {
      const res = await fetch(`${API_BASE}/api/profesores/${id}`, { method: "DELETE" });

      if (res.ok) {
        alert("Docente eliminado");
        if (docenteSeleccionado === id) {
          setDocenteSeleccionado(null);
          setModoEdicion(false);
          resetFormulario();
        }
        await fetchDocentes();
      } else {
        let msg = "No se pudo eliminar el docente";
        try {
          const data = await res.json();
          if (data?.mensaje) msg = data.mensaje;
        } catch {}
        alert(msg);
      }
    } catch (e) {
      console.error("Error eliminando docente:", e);
      alert("Error de conexión al eliminar docente");
    }
  };

  const editarDocente = (docente) => {
    setNuevoDocente({
      usuario: docente.usuario || "",
      contrasena: docente.contrasena || "",
      nombre_completo: docente.nombre_completo || "",
      cedula: docente.cedula || "",
      email: docente.email || "",
      telefono: docente.telefono || "",
      area: docente.area || "informática",
    });
    setDocenteSeleccionado(docente.id);
    setModoEdicion(true);
  };

  const cancelarEdicion = () => {
    resetFormulario();
    setModoEdicion(false);
    setDocenteSeleccionado(null);
  };

  // =========================
  // Modal Ver Docente + colegios asociados
  // =========================
  const abrirModalVer = async (doc) => {
    setDocenteVista(doc);
    setModalVerAbierto(true);

    // cargar colegios asociados
    setCargandoAsociados(true);
    setColegiosAsociadosVista([]);

    try {
      const res = await fetch(`${API_BASE}/api/profesores/${doc.id}/colegios`);
      const data = res.ok ? await res.json() : [];
      setColegiosAsociadosVista(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error cargando colegios asociados:", e);
      setColegiosAsociadosVista([]);
    } finally {
      setCargandoAsociados(false);
    }
  };

  const cerrarModalVer = () => {
    setModalVerAbierto(false);
    setDocenteVista(null);
    setColegiosAsociadosVista([]);
  };

  const desasociarColegio = async (colegio) => {
    if (!docenteVista?.id || !colegio?.id) return;

    const confirmar = window.confirm(
      `¿Desasociar a ${docenteVista.nombre_completo} de "${colegio.nombre}"?\n\n` +
        "Esto no elimina pedidos históricos, solo quita el colegio para nuevos pedidos del docente."
    );
    if (!confirmar) return;

    try {
      const res = await fetch(
        `${API_BASE}/api/profesores/${docenteVista.id}/colegios/${colegio.id}`,
        { method: "DELETE" }
      );

      if (res.ok) {
        setColegiosAsociadosVista((prev) =>
          prev.filter((item) => Number(item.id) !== Number(colegio.id))
        );
      } else {
        let msg = "No se pudo desasociar el colegio";
        try {
          const data = await res.json();
          if (data?.mensaje) msg = data.mensaje;
        } catch {}
        alert(msg);
      }
    } catch (e) {
      console.error("Error desasociando colegio:", e);
      alert("Error de conexión al desasociar colegio");
    }
  };

  // =========================
  // Modal Asociar Colegio
  // =========================
  const abrirModalAsociar = async (doc) => {
    setDocenteAsociar(doc);
    setColegiosSeleccionados([]);
    setColegiosYaAsociados([]);
    setBusquedaColegioAsociar("");
    setModalAsociarAbierto(true);

    try {
      const res = await fetch(`${API_BASE}/api/profesores/${doc.id}/colegios`);
      const data = res.ok ? await res.json() : [];
      setColegiosYaAsociados(Array.isArray(data) ? data.map((item) => Number(item.id)) : []);
    } catch (e) {
      console.error("Error cargando colegios ya asociados:", e);
      setColegiosYaAsociados([]);
    }
  };

  const cerrarModalAsociar = () => {
    setModalAsociarAbierto(false);
    setDocenteAsociar(null);
    setColegiosSeleccionados([]);
    setColegiosYaAsociados([]);
    setBusquedaColegioAsociar("");
  };

  const alternarColegioSeleccionado = (colegioId) => {
    const idNum = Number(colegioId);
    setColegiosSeleccionados((prev) =>
      prev.includes(idNum) ? prev.filter((id) => id !== idNum) : [...prev, idNum]
    );
  };

  const asociarDocente = async () => {
    if (!docenteAsociar?.id || colegiosSeleccionados.length === 0) {
      alert("Seleccioná al menos un colegio");
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/profesores/${docenteAsociar.id}/asociar-multiple`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ colegios_ids: colegiosSeleccionados }),
      });

      if (res.ok) {
        alert("Colegios asociados al docente");
        cerrarModalAsociar();
      } else {
        let msg = "No se pudo asociar el docente";
        try {
          const data = await res.json();
          if (data?.mensaje) msg = data.mensaje;
        } catch {}
        alert(msg);
      }
    } catch (e) {
      console.error("Error asociando docente:", e);
      alert("Error de conexión al asociar docente");
    }
  };

  // =========================
  // ESC para modales
  // =========================
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        if (modalVerAbierto) cerrarModalVer();
        if (modalAsociarAbierto) cerrarModalAsociar();
      }
    };
    if (modalVerAbierto || modalAsociarAbierto) window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [modalVerAbierto, modalAsociarAbierto]);

  // =========================
  // Lista filtrada por búsqueda
  // =========================
  const docentesFiltrados = useMemo(() => {
    const q = (busqueda || "").trim().toLowerCase();
    if (!q) return docentes;

    return (docentes || []).filter((d) => {
      const nombre = (d.nombre_completo || "").toLowerCase();
      const usuario = (d.usuario || "").toLowerCase();
      const cedula = (d.cedula || "").toLowerCase();
      const email = (d.email || "").toLowerCase();
      return (
        nombre.includes(q) ||
        usuario.includes(q) ||
        cedula.includes(q) ||
        email.includes(q)
      );
    });
  }, [docentes, busqueda]);

  const colegiosFiltradosAsociar = useMemo(() => {
    const q = busquedaColegioAsociar.trim().toLowerCase();
    return colegios.filter((colegio) => {
      const nombre = (colegio.nombre || "").toLowerCase();
      return !q || nombre.includes(q);
    });
  }, [colegios, busquedaColegioAsociar]);

  const renderFila = (label, value) => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 py-2 border-b">
      <div className="text-sm font-semibold text-gray-700">{label}</div>
      <div className="md:col-span-2 text-sm text-gray-800 break-words">
        {value && String(value).trim() !== "" ? value : <span className="text-gray-400">-</span>}
      </div>
    </div>
  );

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4 text-blue-700">Gestión de Docentes</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ============================= */}
        {/* SECCIÓN 1: REGISTRO / EDICIÓN */}
        {/* ============================= */}
        <div className="bg-white p-6 shadow rounded-lg space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">
              {modoEdicion ? "Editar docente" : "Registrar nuevo docente"}
            </h3>

            {modoEdicion && (
              <span className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-800">
                Modo edición
              </span>
            )}
          </div>

          {modoEdicion && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded flex items-center justify-between">
              <div className="text-sm">Estás editando un docente.</div>
              <button
                type="button"
                onClick={cancelarEdicion}
                className="bg-gray-700 hover:bg-gray-800 text-white px-3 py-2 rounded"
              >
                Cancelar edición
              </button>
            </div>
          )}

          <form onSubmit={registrarDocente} className="space-y-4">
            <input
              className="w-full border px-3 py-2 rounded"
              type="text"
              name="usuario"
              placeholder="Usuario"
              value={nuevoDocente.usuario}
              onChange={manejarCambio}
              required
            />

            <input
              className="w-full border px-3 py-2 rounded"
              type="password"
              name="contrasena"
              placeholder="Contraseña"
              value={nuevoDocente.contrasena}
              onChange={manejarCambio}
              required
            />

            <input
              className="w-full border px-3 py-2 rounded"
              type="text"
              name="nombre_completo"
              placeholder="Nombre completo"
              value={nuevoDocente.nombre_completo}
              onChange={manejarCambio}
              required
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                className="w-full border px-3 py-2 rounded"
                type="text"
                name="cedula"
                placeholder="Cédula"
                value={nuevoDocente.cedula}
                onChange={manejarCambio}
              />
              <input
                className="w-full border px-3 py-2 rounded"
                type="text"
                name="telefono"
                placeholder="Teléfono"
                value={nuevoDocente.telefono}
                onChange={manejarCambio}
              />
            </div>

            <input
              className="w-full border px-3 py-2 rounded"
              type="email"
              name="email"
              placeholder="Email"
              value={nuevoDocente.email}
              onChange={manejarCambio}
            />

            <select
              className="w-full border px-3 py-2 rounded"
              name="area"
              value={nuevoDocente.area}
              onChange={manejarCambio}
              required
            >
              <option value="informática">Informática</option>
              <option value="robótica">Robótica</option>
              <option value="ambas">Ambas</option>
            </select>

            <div className="flex gap-2">
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
              >
                {modoEdicion ? "Actualizar" : "Registrar"}
              </button>

              {!modoEdicion && (
                <button
                  type="button"
                  onClick={resetFormulario}
                  className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 transition"
                >
                  Limpiar
                </button>
              )}
            </div>
          </form>
        </div>

        {/* ============================= */}
        {/* SECCIÓN 2: DOCENTES REGISTRADOS */}
        {/* ============================= */}
        <div className="bg-white p-6 shadow rounded-lg">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-3">
              <h3 className="text-xl font-semibold">Docentes registrados</h3>
              <span className="text-sm text-gray-500">{docentesFiltrados.length}</span>
            </div>

            {/* ✅ Buscador con “lupa” */}
            <div className="relative w-full max-w-xs">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔎</span>
              <input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar docente..."
                className="w-full border rounded pl-9 pr-3 py-2 text-sm"
              />
            </div>
          </div>

          <ul className="space-y-2">
            {docentesFiltrados.map((doc) => (
              <li key={doc.id} className="bg-white p-4 rounded border flex items-center justify-between">
                <div>
                  <p className="font-semibold">{doc.nombre_completo}</p>
                  <p className="text-sm text-gray-500">
                    {doc.usuario} - <span className="italic text-blue-500">{doc.area}</span>
                  </p>
                </div>

                <div className="space-x-2">
                  {/* ✅ Ver datos */}
                  <button
                    title="Ver datos"
                    onClick={() => abrirModalVer(doc)}
                    className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition"
                  >
                    👁️
                  </button>

                  <button
                    title="Modificar"
                    onClick={() => editarDocente(doc)}
                    className="bg-yellow-400 text-white px-3 py-1 rounded hover:bg-yellow-500 transition"
                  >
                    ✏️
                  </button>

                  {/* ✅ Asociar colegio (MODAL) */}
                  <button
                    title="Asignar colegio"
                    onClick={() => abrirModalAsociar(doc)}
                    className="bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700 transition"
                  >
                    🏫
                  </button>

                  <button
                    title="Eliminar"
                    onClick={() => eliminarDocente(doc.id)}
                    className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition"
                  >
                    🗑️
                  </button>
                </div>
              </li>
            ))}

            {docentesFiltrados.length === 0 && (
              <li className="text-gray-500 text-sm">No se encontraron docentes con ese criterio.</li>
            )}
          </ul>
        </div>
      </div>

      {/* ============================= */}
      {/* MODAL VER DOCENTE */}
      {/* ============================= */}
      {modalVerAbierto && docenteVista && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4"
          onClick={cerrarModalVer}
        >
          <div
            className="w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
              <div>
                <h3 className="text-lg font-bold">Datos del docente</h3>
                <p className="text-sm opacity-90">
                  <span className="font-semibold">{docenteVista.nombre_completo}</span>
                </p>
              </div>
              <button
                onClick={cerrarModalVer}
                className="bg-white bg-opacity-20 hover:bg-opacity-30 px-3 py-1 rounded"
                title="Cerrar"
              >
                ✕
              </button>
            </div>

            <div className="p-5">
              <div className="space-y-1">
                {renderFila("Usuario", docenteVista.usuario)}
                {renderFila("Nombre completo", docenteVista.nombre_completo)}
                {renderFila("Cédula", docenteVista.cedula)}
                {renderFila("Email", docenteVista.email)}
                {renderFila("Teléfono", docenteVista.telefono)}
                {renderFila("Área", docenteVista.area)}
                {docenteVista.activo !== undefined &&
                  renderFila("Activo", Number(docenteVista.activo) === 1 ? "Sí" : "No")}
              </div>

              <div className="mt-4">
                <h4 className="font-semibold text-gray-800 mb-2">Colegios asociados</h4>
                {cargandoAsociados ? (
                  <p className="text-sm text-gray-600">Cargando colegios asociados...</p>
                ) : (
                  <>
                    {colegiosAsociadosVista.length === 0 ? (
                      <p className="text-sm text-gray-500">No tiene colegios asociados.</p>
                    ) : (
                      <div className="grid gap-2">
                        {colegiosAsociadosVista.map((c) => (
                          <div
                            key={c.id}
                            className="flex items-center justify-between gap-3 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2"
                          >
                            <span className="text-sm font-semibold text-blue-800">{c.nombre}</span>
                            <button
                              type="button"
                              onClick={() => desasociarColegio(c)}
                              className="rounded bg-white px-3 py-1 text-xs font-bold text-red-700 border border-red-100 hover:bg-red-50"
                              title="Desasociar colegio"
                            >
                              Quitar
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={cerrarModalVer}
                  className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                >
                  Cerrar
                </button>
              </div>

              <p className="mt-2 text-xs text-gray-500">
                Tip: también podés cerrar con <span className="font-semibold">ESC</span> o clic fuera.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ============================= */}
      {/* MODAL ASOCIAR COLEGIO */}
      {/* ============================= */}
      {modalAsociarAbierto && docenteAsociar && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4"
          onClick={cerrarModalAsociar}
        >
          <div
            className="w-full max-w-lg bg-white rounded-xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-indigo-600 to-blue-600 text-white">
              <div>
                <h3 className="text-lg font-bold">Asignar colegio</h3>
                <p className="text-sm opacity-90">
                  Docente: <span className="font-semibold">{docenteAsociar.nombre_completo}</span>
                </p>
              </div>
              <button
                onClick={cerrarModalAsociar}
                className="bg-white bg-opacity-20 hover:bg-opacity-30 px-3 py-1 rounded"
                title="Cerrar"
              >
                ✕
              </button>
            </div>

            <div className="p-5 space-y-3">
              <label className="block text-sm font-semibold text-gray-700">Colegios</label>
              <input
                className="w-full border px-3 py-2 rounded"
                placeholder="Buscar colegio..."
                value={busquedaColegioAsociar}
                onChange={(e) => setBusquedaColegioAsociar(e.target.value)}
              />

              <div className="border rounded max-h-80 overflow-y-auto divide-y">
                {colegiosFiltradosAsociar.map((c) => {
                  const idNum = Number(c.id);
                  const yaAsociado = colegiosYaAsociados.includes(idNum);

                  return (
                    <label
                      key={c.id}
                      className={`flex items-center gap-3 px-3 py-2 text-sm ${
                        yaAsociado ? "bg-gray-50 text-gray-400" : "hover:bg-blue-50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={yaAsociado || colegiosSeleccionados.includes(idNum)}
                        disabled={yaAsociado}
                        onChange={() => alternarColegioSeleccionado(idNum)}
                      />
                      <span>{c.nombre}</span>
                      {yaAsociado && <span className="ml-auto text-xs">Ya asociado</span>}
                    </label>
                  );
                })}

                {colegiosFiltradosAsociar.length === 0 && (
                  <div className="px-3 py-3 text-sm text-gray-500">No se encontraron colegios.</div>
                )}
              </div>

              <p className="text-xs text-gray-500">
                Seleccionados: {colegiosSeleccionados.length}
              </p>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={cerrarModalAsociar}
                  className="px-4 py-2 rounded border hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={asociarDocente}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition"
                  disabled={colegiosSeleccionados.length === 0}
                >
                  Asociar seleccionados
                </button>
              </div>

              <p className="text-xs text-gray-500">
                Tip: también podés cerrar con <span className="font-semibold">ESC</span> o clic fuera.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionDocentes;
